<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useCurrency } from 'dashboard/composables/useCurrency';
import catalogProductsAPI from 'dashboard/api/catalogProducts';

import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const { formatCurrency: formatCurrencyValue } = useCurrency();

const loading = ref(false);
const saving = ref(false);
const products = ref([]);
const showForm = ref(false);
const editing = ref(null);

const emptyForm = () => ({
  name: '',
  description: '',
  unit_price: 0,
  recurrence: 'monthly',
  code: '',
  active: true,
});
const form = ref(emptyForm());

const recurrenceOptions = computed(() => [
  { value: 'monthly', label: t('PRODUCTS_CATALOG.RECURRENCE.MONTHLY') },
  { value: 'yearly', label: t('PRODUCTS_CATALOG.RECURRENCE.YEARLY') },
  { value: 'one_time', label: t('PRODUCTS_CATALOG.RECURRENCE.ONE_TIME') },
]);

const formatCurrency = value => formatCurrencyValue(parseFloat(value) || 0);

const recurrenceSuffix = recurrence => {
  if (recurrence === 'monthly') return t('PRODUCTS_CATALOG.SUFFIX.MONTHLY');
  if (recurrence === 'yearly') return t('PRODUCTS_CATALOG.SUFFIX.YEARLY');
  return '';
};

// MRR = mensais + anuais/12; avulso somado à parte, para dar a visão de
// "quanto estamos produzindo" recorrente no catálogo.
const mrrTotal = computed(() =>
  products.value
    .filter(p => p.active)
    .reduce((sum, p) => {
      const price = parseFloat(p.unit_price) || 0;
      if (p.recurrence === 'monthly') return sum + price;
      if (p.recurrence === 'yearly') return sum + price / 12;
      return sum;
    }, 0)
);

const fetchProducts = async () => {
  loading.value = true;
  try {
    const { data } = await catalogProductsAPI.get();
    products.value = data;
  } catch (error) {
    useAlert(t('PRODUCTS_CATALOG.API.FETCH_ERROR'));
  } finally {
    loading.value = false;
  }
};

const openAdd = () => {
  editing.value = null;
  form.value = emptyForm();
  showForm.value = true;
};

const openEdit = product => {
  editing.value = product;
  form.value = {
    name: product.name,
    description: product.description || '',
    unit_price: parseFloat(product.unit_price) || 0,
    recurrence: product.recurrence || 'monthly',
    code: product.code || '',
    active: product.active,
  };
  showForm.value = true;
};

const closeForm = () => {
  showForm.value = false;
  editing.value = null;
};

const saveProduct = async () => {
  if (!form.value.name || parseFloat(form.value.unit_price) < 0) {
    useAlert(t('PRODUCTS_CATALOG.API.VALIDATION_ERROR'));
    return;
  }
  saving.value = true;
  try {
    const payload = {
      ...form.value,
      unit_price: parseFloat(form.value.unit_price),
    };
    if (editing.value) {
      const { data } = await catalogProductsAPI.update(
        editing.value.id,
        payload
      );
      const idx = products.value.findIndex(p => p.id === editing.value.id);
      if (idx !== -1) products.value[idx] = data;
      useAlert(t('PRODUCTS_CATALOG.API.UPDATE_SUCCESS'));
    } else {
      const { data } = await catalogProductsAPI.create(payload);
      products.value.unshift(data);
      useAlert(t('PRODUCTS_CATALOG.API.CREATE_SUCCESS'));
    }
    closeForm();
  } catch (error) {
    const message =
      error?.response?.data?.errors?.join(', ') ||
      t('PRODUCTS_CATALOG.API.SAVE_ERROR');
    useAlert(message);
  } finally {
    saving.value = false;
  }
};

const deleteProduct = async product => {
  // eslint-disable-next-line no-alert
  if (
    !window.confirm(
      t('PRODUCTS_CATALOG.CONFIRM_DELETE', { name: product.name })
    )
  )
    return;
  try {
    await catalogProductsAPI.delete(product.id);
    products.value = products.value.filter(p => p.id !== product.id);
    useAlert(t('PRODUCTS_CATALOG.API.DELETE_SUCCESS'));
  } catch (error) {
    useAlert(t('PRODUCTS_CATALOG.API.DELETE_ERROR'));
  }
};

onMounted(fetchProducts);
</script>

<template>
  <SettingsLayout
    :is-loading="loading"
    :loading-message="t('PRODUCTS_CATALOG.LOADING')"
    :no-records-found="!products.length && !showForm"
    :no-records-message="t('PRODUCTS_CATALOG.EMPTY')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="t('PRODUCTS_CATALOG.TITLE')"
        :description="t('PRODUCTS_CATALOG.DESCRIPTION')"
      >
        <template #actions>
          <Button
            icon="i-lucide-plus"
            :label="t('PRODUCTS_CATALOG.ADD')"
            @click="openAdd"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <div class="flex flex-col gap-4">
        <!-- Resumo MRR do catálogo -->
        <div
          v-if="mrrTotal > 0"
          class="flex items-center justify-between px-4 py-3 rounded-xl border border-n-weak bg-n-alpha-1"
        >
          <span class="text-sm font-medium text-n-slate-11">
            {{ t('PRODUCTS_CATALOG.MRR_SUMMARY') }}
          </span>
          <span class="text-lg font-semibold text-n-slate-12 tabular-nums">
            {{ formatCurrency(mrrTotal)
            }}<span class="text-sm text-n-slate-10">{{
              t('PRODUCTS_CATALOG.SUFFIX.MONTHLY')
            }}</span>
          </span>
        </div>

        <!-- Formulário inline -->
        <div
          v-if="showForm"
          class="flex flex-col gap-3 p-4 rounded-xl border border-n-weak bg-n-solid-2"
        >
          <h4 class="text-sm font-semibold text-n-slate-12">
            {{
              editing ? t('PRODUCTS_CATALOG.EDIT') : t('PRODUCTS_CATALOG.ADD')
            }}
          </h4>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label class="flex flex-col gap-1 md:col-span-2">
              <span class="text-xs font-medium text-n-slate-11"
                >{{ t('PRODUCTS_CATALOG.FORM.NAME') }} *</span
              >
              <input
                v-model="form.name"
                type="text"
                class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:border-n-brand"
                :placeholder="t('PRODUCTS_CATALOG.FORM.NAME_PLACEHOLDER')"
              />
            </label>
            <label class="flex flex-col gap-1">
              <span class="text-xs font-medium text-n-slate-11"
                >{{ t('PRODUCTS_CATALOG.FORM.PRICE') }} *</span
              >
              <input
                v-model.number="form.unit_price"
                type="number"
                step="0.01"
                min="0"
                class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:border-n-brand"
              />
              <span v-if="form.unit_price > 0" class="text-xs text-n-slate-10">
                {{ formatCurrency(form.unit_price)
                }}{{ recurrenceSuffix(form.recurrence) }}
              </span>
            </label>
            <label class="flex flex-col gap-1">
              <span class="text-xs font-medium text-n-slate-11">{{
                t('PRODUCTS_CATALOG.FORM.RECURRENCE')
              }}</span>
              <select
                v-model="form.recurrence"
                class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:border-n-brand"
              >
                <option
                  v-for="opt in recurrenceOptions"
                  :key="opt.value"
                  :value="opt.value"
                >
                  {{ opt.label }}
                </option>
              </select>
            </label>
            <label class="flex flex-col gap-1">
              <span class="text-xs font-medium text-n-slate-11">{{
                t('PRODUCTS_CATALOG.FORM.CODE')
              }}</span>
              <input
                v-model="form.code"
                type="text"
                class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:border-n-brand"
                :placeholder="t('PRODUCTS_CATALOG.FORM.CODE_PLACEHOLDER')"
              />
            </label>
            <label class="flex items-center gap-2 md:col-span-1 mt-5">
              <input v-model="form.active" type="checkbox" class="rounded" />
              <span class="text-sm text-n-slate-11">{{
                t('PRODUCTS_CATALOG.FORM.ACTIVE')
              }}</span>
            </label>
            <label class="flex flex-col gap-1 md:col-span-2">
              <span class="text-xs font-medium text-n-slate-11">{{
                t('PRODUCTS_CATALOG.FORM.DESCRIPTION')
              }}</span>
              <textarea
                v-model="form.description"
                rows="2"
                class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:border-n-brand resize-none"
              />
            </label>
          </div>
          <div class="flex justify-end gap-2">
            <Button
              variant="clear"
              :label="t('PRODUCTS_CATALOG.FORM.CANCEL')"
              @click="closeForm"
            />
            <Button
              color="teal"
              :is-loading="saving"
              :label="
                editing
                  ? t('PRODUCTS_CATALOG.FORM.SAVE')
                  : t('PRODUCTS_CATALOG.FORM.CREATE')
              "
              @click="saveProduct"
            />
          </div>
        </div>

        <!-- Tabela de produtos -->
        <div
          v-if="products.length"
          class="overflow-x-auto rounded-xl border border-n-weak"
        >
          <table class="w-full text-sm">
            <thead>
              <tr
                class="text-left text-[11px] font-semibold uppercase tracking-wide text-n-slate-10 bg-n-alpha-1"
              >
                <th class="px-4 py-3">
                  {{ t('PRODUCTS_CATALOG.TABLE.NAME') }}
                </th>
                <th class="px-4 py-3">
                  {{ t('PRODUCTS_CATALOG.TABLE.RECURRENCE') }}
                </th>
                <th class="px-4 py-3 text-right">
                  {{ t('PRODUCTS_CATALOG.TABLE.PRICE') }}
                </th>
                <th class="px-4 py-3">
                  {{ t('PRODUCTS_CATALOG.TABLE.STATUS') }}
                </th>
                <th class="px-4 py-3 text-right">
                  {{ t('PRODUCTS_CATALOG.TABLE.ACTIONS') }}
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-n-weak">
              <tr
                v-for="product in products"
                :key="product.id"
                class="text-n-slate-12"
              >
                <td class="px-4 py-3">
                  <div class="font-medium">{{ product.name }}</div>
                  <div v-if="product.code" class="text-xs text-n-slate-10">
                    {{ product.code }}
                  </div>
                </td>
                <td class="px-4 py-3 text-n-slate-11">
                  {{
                    recurrenceOptions.find(o => o.value === product.recurrence)
                      ?.label
                  }}
                </td>
                <td class="px-4 py-3 text-right font-medium tabular-nums">
                  {{ formatCurrency(product.unit_price)
                  }}<span class="text-xs text-n-slate-10">{{
                    recurrenceSuffix(product.recurrence)
                  }}</span>
                </td>
                <td class="px-4 py-3">
                  <span
                    class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                    :class="
                      product.active
                        ? 'bg-n-teal-3 text-n-teal-11'
                        : 'bg-n-slate-3 text-n-slate-10'
                    "
                  >
                    {{
                      product.active
                        ? t('PRODUCTS_CATALOG.ACTIVE')
                        : t('PRODUCTS_CATALOG.INACTIVE')
                    }}
                  </span>
                </td>
                <td class="px-4 py-3">
                  <div class="flex items-center justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="xs"
                      icon="i-lucide-pencil"
                      @click="openEdit(product)"
                    />
                    <Button
                      variant="ghost"
                      size="xs"
                      color="ruby"
                      icon="i-lucide-trash-2"
                      @click="deleteProduct(product)"
                    />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>

<style scoped>
. {
  all: revert;
}
. {
  display: block;
}
</style>
